import type { Nominal } from './typeHelpers.no-deps'

/*
 * Transforms an array of objects to an js Map using a particular property as key
 */
export function arrayToMap<T, K extends keyof T>(array: T[], index: K) {
    return new Map<T[K], T>(array.map((item) => [item[index], item] as [T[K], T]))
}

export function arrayToDirectMap<T, K extends keyof T, J extends keyof T>(array: T[], index: K, value: J) {
    return new Map<T[K], T[J]>(array.map((item) => [item[index], item[value]] as [T[K], T[J]]))
}

/**
 * An ID that identifies some deferred task. Pass it to `clearTimeout` to cancel
 * the task.
 */
export type TimeoutId = Nominal<number, 'timeoutID'>
/**
 * A type-safe wrapper around `WindowOrWorkerGlobalScope.setTimeout()`.
 *
 * @param callback A function to be executed after the timer expires
 * @param delay The time, in milliseconds, the timer should wait before calling
 * the callback
 * @param args Additional arguments passed to the callback
 * @returns An ID that can be passed to `clearTimeout` to cancel a scheduled
 * callback
 */
export function setTimeout<F extends (...args: any[]) => void>(
    callback: F,
    delay?: number,
    ...args: Parameters<typeof callback>
): TimeoutId {
    return globalThis.setTimeout(callback, delay, ...args) as unknown as TimeoutId
}

/**
 * A type-safe wrapper around `WindowOrWorkerGlobalScope.clearTimeout()`.
 *
 * @param timeoutId The ID of the timeout to cancel
 */
export function clearTimeout(timeoutId: TimeoutId) {
    globalThis.clearTimeout(timeoutId as any)
}

/**
 * Return nothing after some time.
 *
 * A Promise-based API for `setTimeout`. Use this to delay execution of some
 * callback, or to put an async function to sleep for a little while.
 *
 * @param milliseconds Number of milliseconds to sleep for
 */
export async function wait(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

type ObjectFromEntries<K extends PropertyKey, V> = {
    [P in K]: V
}

/**
 * TODO: Polyfill for Object.fromEntries, available in ES2019. Deprecate this
 * function when target is upgraded.
 *
 * Returns an object created by key-value entries for properties and methods
 *
 * @param entries An iterable object that contains key-value entries for
 *        properties and methods.
 */
export function fromEntries<K extends PropertyKey, V = any>(
    entries: Iterable<readonly [K, V]>
): ObjectFromEntries<K, V> {
    const obj: ObjectFromEntries<K, V> = Object.create({})
    for (const [key, value] of entries) {
        obj[key] = value
    }
    return obj
}

/**
 * Split up a {@linkcode Record} into smaller pages
 *
 * @param record The record to split up
 * @param size The maximum number of entries in each page
 * @returns An {@linkcode Iterable} yielding pages of the record given
 */
export function* paginateRecord<K extends PropertyKey, V>(
    record: Record<K, V>,
    size: number
): Iterable<ObjectFromEntries<K, V>> {
    const regularEntries = Object.entries(record) as Array<[K, V]>
    const symbolicEntries = Object.getOwnPropertySymbols(record).map((symbol) => [
        symbol,
        (record as any)[symbol],
    ]) as Array<[K, V]>
    const entries = [...regularEntries, ...symbolicEntries]
    for (let pageNumber = 0; pageNumber * size < entries.length; pageNumber++) {
        const chunk = entries.slice(pageNumber * size, (pageNumber + 1) * size)
        yield fromEntries(chunk)
    }
}

/**
 * Useful for converting query params which can be both single and array to array.
 * (workaround until we can switch to joi.array().single())
 */
export function ensureArray<T>(param: T | T[] | null): T[] | null {
    if (param != null) {
        return Array.isArray(param) ? param : [param]
    }
    return null
}
